/**
 * ImageCropModal — shared pan/zoom crop modal (web only).
 *
 * Extracted from the original avatar crop (CORE-326) and generalised for a
 * second consumer (CORE-327: manifesto poster). The user pans an image inside
 * a fixed viewport, zooms in/out, sees the live preview, then confirms — at
 * which point the chosen region is rendered to an off-screen canvas and handed
 * back as a ready-cropped JPEG Blob plus a fresh object-URL for the preview.
 * The backend upload paths accept a Blob/File as-is, so no API change is
 * needed and no crop coordinates are stored server-side.
 *
 * The viewport can be square+circular (avatar) or any rectangle (16:9 poster).
 * Cover-fit keeps the image filling the viewport at zoom = 1, so there is never
 * an empty edge inside the crop frame.
 *
 * Web only. On native, image pickers open the OS crop editor (pan + zoom)
 * directly, so callers don't open this modal off-web; if it somehow renders
 * there it no-ops to null.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Image,
  Modal,
  PanResponder,
  Platform,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';
import { Typography } from '../ui';

import { accentTextOn, ON_ACCENT_TEXT } from '../../styles/accentText';
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

export interface ImageCropModalProps {
  /** Source image URI (data: / blob:). Null = closed. */
  imageUri: string | null;
  onCancel: () => void;
  /** Receives the cropped JPEG and a fresh object-URL for the preview. */
  onConfirm: (blob: Blob, previewUri: string) => void;
  /** Viewport (visible crop frame) size in px. */
  viewportW: number;
  viewportH: number;
  /** Exported image size in px (the file actually sent to the backend). */
  outputW: number;
  outputH: number;
  /** 'circle' masks the (square) viewport to a circle; 'rect' keeps it square-cornered. */
  shape?: 'circle' | 'rect';
  title: string;
  hint?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Prefix for testIDs: `${testIDPrefix}-modal`, `-viewport`, `-zoom-in`, etc. */
  testIDPrefix: string;
  /** JPEG quality 0..1 for the exported crop. */
  quality?: number;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageUri,
  onCancel,
  onConfirm,
  viewportW,
  viewportH,
  outputW,
  outputH,
  shape = 'rect',
  title,
  hint,
  confirmLabel = 'Сохранить',
  cancelLabel = 'Отмена',
  testIDPrefix,
  quality = 0.9,
}) => {
  const { isDark } = useTheme();

  // Natural pixel size of the source image — drives the cover-fit baseline.
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);

  // Live refs so the PanResponder closure (created once) reads current values.
  const offsetRef = useRef(offset);
  const zoomRef = useRef(zoom);
  const naturalRef = useRef(natural);
  offsetRef.current = offset;
  zoomRef.current = zoom;
  naturalRef.current = natural;

  const visible = imageUri != null;
  const radius = shape === 'circle' ? Math.min(viewportW, viewportH) / 2 : 14;

  // (Re)load natural dimensions whenever a new image is handed in. On web we
  // create a DOM Image (also reused at export time) — same-origin blob:/data:
  // URIs never taint the canvas, so toBlob() stays available.
  useEffect(() => {
    if (!visible || Platform.OS !== 'web' || typeof window === 'undefined') return;
    let cancelled = false;
    setNatural(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    const img = new window.Image();
    img.onload = () => {
      if (!cancelled) setNatural({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
    };
    img.src = imageUri as string;
    return () => { cancelled = true; };
  }, [imageUri, visible]);

  // cover-fit scale so the image always fills BOTH viewport dims at zoom = 1.
  const baseScale = useMemo(() => {
    if (!natural) return 1;
    return Math.max(viewportW / natural.w, viewportH / natural.h);
  }, [natural, viewportW, viewportH]);

  const display = useMemo(() => {
    if (!natural) return { w: viewportW, h: viewportH };
    const s = baseScale * zoom;
    return { w: natural.w * s, h: natural.h * s };
  }, [natural, baseScale, zoom, viewportW, viewportH]);

  // Keep the image covering the viewport — never let an edge slip inside.
  const clampOffset = useCallback((o: { x: number; y: number }, disp: { w: number; h: number }) => {
    const maxX = Math.max(0, (disp.w - viewportW) / 2);
    const maxY = Math.max(0, (disp.h - viewportH) / 2);
    return { x: clamp(o.x, -maxX, maxX), y: clamp(o.y, -maxY, maxY) };
  }, [viewportW, viewportH]);

  // Re-clamp the pan whenever zoom shrinks the image back toward the viewport.
  useEffect(() => {
    setOffset((o) => clampOffset(o, display));
  }, [display, clampOffset]);

  const panResponder = useMemo(() => {
    const startOffset = { x: 0, y: 0 };
    return PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        startOffset.x = offsetRef.current.x;
        startOffset.y = offsetRef.current.y;
      },
      onPanResponderMove: (_e, g) => {
        const nat = naturalRef.current;
        if (!nat) return;
        const s = Math.max(viewportW / nat.w, viewportH / nat.h) * zoomRef.current;
        const disp = { w: nat.w * s, h: nat.h * s };
        const maxX = Math.max(0, (disp.w - viewportW) / 2);
        const maxY = Math.max(0, (disp.h - viewportH) / 2);
        setOffset({
          x: clamp(startOffset.x + g.dx, -maxX, maxX),
          y: clamp(startOffset.y + g.dy, -maxY, maxY),
        });
      },
    });
  }, [viewportW, viewportH]);

  const stepZoom = useCallback((dir: 1 | -1) => {
    setZoom((z) => clamp(Number((z + dir * ZOOM_STEP).toFixed(3)), MIN_ZOOM, MAX_ZOOM));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !natural || !imageUri) return;
    setBusy(true);
    try {
      const s = baseScale * zoom;
      const disp = { w: natural.w * s, h: natural.h * s };
      const o = clampOffset(offset, disp);
      // Top-left of the displayed image within the viewport (origin at 0,0).
      const left = (viewportW - disp.w) / 2 + o.x;
      const top = (viewportH - disp.h) / 2 + o.y;
      // Map the viewport rectangle back into the source image's natural pixels.
      const srcX = -left / s;
      const srcY = -top / s;
      const srcW = viewportW / s;
      const srcH = viewportH / s;

      const img = new window.Image();
      const loaded: Promise<void> = new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('image load failed'));
      });
      img.src = imageUri;
      await loaded;

      const canvas = document.createElement('canvas');
      canvas.width = outputW;
      canvas.height = outputH;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no 2d context');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outputW, outputH);

      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/jpeg', quality),
      );
      if (!blob) throw new Error('toBlob returned null');
      const previewUri = URL.createObjectURL(blob);
      onConfirm(blob, previewUri);
    } catch {
      // Surface nothing fancy — fall back to closing; the user can retry.
      onCancel();
    } finally {
      setBusy(false);
    }
  }, [natural, imageUri, baseScale, zoom, offset, clampOffset, onConfirm, onCancel, viewportW, viewportH, outputW, outputH, quality]);

  if (!visible || Platform.OS !== 'web') return null;

  const tc = {
    surface: isDark ? '#1A1A1A' : '#FFFFFF',
    text: isDark ? '#F5F0EB' : '#2B2730',
    textSec: isDark ? 'rgba(245,240,235,0.7)' : 'rgba(15,20,25,0.55)',
    border: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)',
  };

  // Modal panel wide enough to host the viewport plus padding.
  const panelWidth = Math.max(320, viewportW + 40);

  return (
    <Modal transparent animationType="fade" visible onRequestClose={onCancel}>
      <View
        testID={`${testIDPrefix}-modal`}
        style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      >
        <View style={{ width: panelWidth, maxWidth: '100%', backgroundColor: tc.surface, borderRadius: 20, padding: 20, alignItems: 'center' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: 16 }}>
            <Typography variant="h3" style={{ fontSize: 18, fontWeight: '700', color: tc.text } as any}>
              {title}
            </Typography>
            <TouchableOpacity onPress={onCancel} testID={`${testIDPrefix}-cancel`}>
              <Ionicons name="close" size={22} color={tc.text} />
            </TouchableOpacity>
          </View>

          {/* Viewport — pan target. */}
          <View
            {...panResponder.panHandlers}
            testID={`${testIDPrefix}-viewport`}
            style={{
              width: viewportW,
              height: viewportH,
              borderRadius: radius,
              overflow: 'hidden',
              backgroundColor: '#000',
              ...(Platform.OS === 'web' ? ({ cursor: 'move', touchAction: 'none' } as any) : {}),
            }}
          >
            {natural && imageUri ? (
              <Image
                source={{ uri: imageUri }}
                style={{
                  position: 'absolute',
                  width: display.w,
                  height: display.h,
                  left: (viewportW - display.w) / 2 + offset.x,
                  top: (viewportH - display.h) / 2 + offset.y,
                }}
                resizeMode="cover"
              />
            ) : null}
            {/* Subtle ring so the crop boundary reads clearly. */}
            <View
              pointerEvents="none"
              style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                borderRadius: radius,
                borderWidth: 2,
                borderColor: 'rgba(255,255,255,0.6)',
              }}
            />
          </View>

          <Text style={{ color: tc.textSec, fontSize: 12, marginTop: 12, textAlign: 'center' }}>
            {hint || 'Перетащите изображение и выберите масштаб'}
          </Text>

          {/* Zoom controls. */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 12, width: '100%' }}>
            <TouchableOpacity
              onPress={() => stepZoom(-1)}
              testID={`${testIDPrefix}-zoom-out`}
              disabled={zoom <= MIN_ZOOM}
              style={{ width: 40, height: 40, borderRadius: 20, borderWidth: 1, borderColor: tc.border, alignItems: 'center', justifyContent: 'center', opacity: zoom <= MIN_ZOOM ? 0.4 : 1, ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}) }}
            >
              <Ionicons name="remove" size={20} color={tc.text} />
            </TouchableOpacity>

            {Platform.OS === 'web' ? (
              <input
                data-testid={`${testIDPrefix}-zoom-slider`}
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.05}
                value={zoom}
                onChange={(e: any) => setZoom(clamp(Number(e.target.value), MIN_ZOOM, MAX_ZOOM))}
                style={{ flex: 1, accentColor: '#E98245' } as any}
              />
            ) : null}

            <TouchableOpacity
              onPress={() => stepZoom(1)}
              testID={`${testIDPrefix}-zoom-in`}
              disabled={zoom >= MAX_ZOOM}
              style={{ width: 40, height: 40, borderRadius: 20, borderWidth: 1, borderColor: tc.border, alignItems: 'center', justifyContent: 'center', opacity: zoom >= MAX_ZOOM ? 0.4 : 1, ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}) }}
            >
              <Ionicons name="add" size={20} color={tc.text} />
            </TouchableOpacity>
          </View>

          {/* Actions. */}
          <View style={{ flexDirection: 'row', gap: 12, marginTop: 20, width: '100%' }}>
            <TouchableOpacity
              onPress={onCancel}
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel={cancelLabel}
              style={{ flex: 1, paddingVertical: 12, borderRadius: 999, borderWidth: 1.5, borderColor: '#F26A36', backgroundColor: 'rgba(255,247,241,0.72)', alignItems: 'center', ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}) }}
            >
              <Text style={{ color: accentTextOn('#D9602F'), fontWeight: '700', fontSize: 14 }}>{cancelLabel}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => void handleConfirm()}
              disabled={busy || !natural}
              testID={`${testIDPrefix}-confirm`}
              style={{ flex: 1, paddingVertical: 12, borderRadius: 12, backgroundColor: '#E98245', alignItems: 'center', opacity: busy || !natural ? 0.6 : 1, ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}) }}
            >
              <Text style={{ color: ON_ACCENT_TEXT, fontWeight: '700', fontSize: 14 }}>{confirmLabel}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};
